import React from 'react';
import styled from 'styled-components';
import Loader from '../../../atoms/Loader';
import { exceedsMaxLength } from './maxLength';
import {
  capacityChars,
  fieldDisplayName,
  isFieldFilled,
  isRequiredField,
  isResultUnanswered,
  isStatusDrivenResult,
  refKey,
  splitRefKey,
  toggleGroupItem,
  FIELD_RIGHT_MARGIN_MM,
} from '../fillLayout';
import { buildElements, BuilderElement } from '../PreviewTool/builder/elements';
import { DEFAULT_MAX_LINES } from '../docOvelayTool';
import { formFontPt } from '../PreviewTool/thresholdConstants';
import {
  BASE_H,
  BASE_W,
  LINE_HEIGHT,
  PAGE_W_MM,
  PT_TO_MM,
  mmToPx,
  ptToPx,
} from '../PreviewTool/builder/coords';

export interface FormFillCanvasHandle {
  scrollToPage: (index: number) => void;
}

interface Props {
  pages: Array<{ base64: string }>;
  data: Record<string, any>;
  setData: (d: Record<string, any>) => void;
  onBlur: (markup?: any) => void;
  // Usable field widths in mm, per page key (fillLayout.computeFitWidths).
  fitWidthsByPage: Record<string, Record<string, number>>;
  // Composite `page::field` keys — a field name is only unique within its page.
  activeKey: string | null;
  setActiveKey: (key: string | null) => void;
  registerRef: (key: string, el: HTMLElement | null) => void;
  onAdvance: (key: string) => void;
  onRetreat: (key: string) => void;
  // Multiplier applied on top of fit-to-width. 1 = fit.
  zoomStep: number;
  onVisiblePage: (index: number) => void;
  // Reports readiness BOTH ways — true once the panel is measured and the first
  // page image has decoded, false again whenever that stops holding. A one-shot
  // "onReady" deadlocked: the parent re-armed its spinner on its own (a markup
  // or form change) while this component stayed mounted and already-ready, so
  // nothing ever fired a second time and the spinner never came down.
  onReadyChange: (ready: boolean) => void;
}

// jsPDF's default font. Matching it keeps what you type looking like what prints.
const PDF_FONT = 'Helvetica, Arial, sans-serif';
const MIN_FONT_PT = 4;
// jsPDF's default line-height factor, so wrapped lines here sit where they will
// sit in the generated PDF.
const PDF_LINE_HEIGHT = 1.15;
// Vertical space between stacked pages.
const PAGE_GAP = 18;
// Diameter of the amber required star (RequiredBadge), in stage pixels.
const BADGE_PX = 11;
// Room left above a page when jumping to it, so the form's sticky toolbar
// (FillThresholdReport's HeaderBar) does not cover the page's top edge.
const PAGE_JUMP_OFFSET = 84;

// Required fields (marked in the form builder) say so on hover, always carry
// the amber star badge, and are washed amber while blank — the same amber the
// builder uses, so a field looks required in both screens. Optional blanks get
// a neutral tint, so amber on this page only ever means "required".
const fieldTitle = (key: string, item: any): string =>
  `${fieldDisplayName(key, item)}${isRequiredField(key, item) ? ' (required)' : ''}`;

// The canvas has no vertical scroll of its own: it sits under the inspection
// details on one page, and the editor's panel is the single scroller. Page
// jumps and page-follow work against that panel, found by walking up.
const findScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  let el = node ? node.parentElement : null;
  while (el) {
    const { overflowY } = window.getComputedStyle(el);
    if (overflowY === 'auto' || overflowY === 'scroll') return el;
    el = el.parentElement;
  }
  return null;
};

// Converts a millimetre LENGTH (not a coordinate) to base-canvas px.
const mmLen = (mm: number) => (mm / PAGE_W_MM) * BASE_W;

// Keeps a cell's text clear of the printed rule on either side.
const CELL_GUTTER_MM = 1;

// Shared 2D context for text measurement, so the canvas can reproduce
// overlayInfo's shrink-to-fit instead of letting long values overflow.
let measureCtx: CanvasRenderingContext2D | null = null;
const measureText = (text: string, fontPx: number): number => {
  if (!measureCtx && typeof document !== 'undefined') {
    measureCtx = document.createElement('canvas').getContext('2d');
  }
  if (!measureCtx) return 0;
  measureCtx.font = `${fontPx}px ${PDF_FONT}`;
  return measureCtx.measureText(text).width;
};

// How many characters a table cell can still hold once the font has shrunk as
// far as it is allowed to. An admin-set Max characters on the table wins.
//
// Without this an inspector could keep typing past the point where shrinking
// helps, and the value ran over the neighbouring column both on screen and in
// the generated PDF.
const cellCharLimit = (boxPx: number, item: any): number => {
  const explicit = Number(item && item.max);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  const refPx = measureText('0', ptToPx(MIN_FONT_PT));
  return refPx > 0 ? Math.max(1, Math.floor(boxPx / refPx)) : 0;
};

// An input's box is taller than its glyphs so it stays clickable; shift it up by
// the ascent PLUS half the extra leading so the text baseline still lands on the
// stored coordinate, exactly where doc.text() will draw it.
const BASELINE_RATIO = 0.8;
const topForBaseline = (baselineY: number, fontPx: number) =>
  baselineY - fontPx * (BASELINE_RATIO + (LINE_HEIGHT - 1) / 2);

// Mirrors overlayInfo's shrink-to-fit.
const fitFont = (text: string, fontPx: number, boxPx: number) => {
  const textPx = measureText(text, fontPx);
  if (boxPx > 0 && textPx > boxPx) {
    return { fontPx: Math.max(ptToPx(MIN_FONT_PT), fontPx * (boxPx / textPx)), shrunk: true };
  }
  return { fontPx, shrunk: false };
};

// Track the rendered width so pages can be drawn at a fixed base size and
// scaled: positions and font sizes then live in one coordinate system.
const useElementWidth = () => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = React.useState(0);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const update = () => setWidth(el.clientWidth);
    update();

    const RO = (window as any).ResizeObserver;
    if (!RO) {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new RO(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
};

// Every page of the scanned form, stacked in ONE scroll area and filled in
// directly. Reaching the bottom of a page carries straight on into the next, the
// way a PDF viewer behaves — rendering a single page at a time left nothing
// below to scroll into.
const FormFillCanvas = React.forwardRef<FormFillCanvasHandle, Props>((props, handleRef) => {
  const {
    pages,
    data,
    setData,
    onBlur,
    fitWidthsByPage,
    activeKey,
    setActiveKey,
    registerRef,
    onAdvance,
    onRetreat,
    zoomStep,
    onVisiblePage,
    onReadyChange,
  } = props;

  // Step to the next/previous field from wherever the caret is. Alt+Arrow works
  // everywhere; Enter/Shift+Enter too, except inside a wrapped field where
  // Enter has to stay a newline.
  //
  // `allowEnter` also does double duty: consuming Enter stops the modal's
  // implicit form submission, which would otherwise close it mid-edit.
  const navKeyDown = (composite: string, allowEnter: boolean) => (e: React.KeyboardEvent) => {
    if (e.altKey && e.key === 'ArrowDown') {
      e.preventDefault();
      onAdvance(composite);
      return;
    }
    if (e.altKey && e.key === 'ArrowUp') {
      e.preventDefault();
      onRetreat(composite);
      return;
    }
    if (e.key !== 'Enter') return;
    if (e.shiftKey) {
      e.preventDefault();
      onRetreat(composite);
    } else if (allowEnter) {
      e.preventDefault();
      onAdvance(composite);
    }
  };

  const { ref: hostRef, width } = useElementWidth();
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const [loadedPages, setLoadedPages] = React.useState<Record<string, boolean>>({});

  // Swapping the inspection type swaps the page images while this component
  // stays mounted. Without clearing, page 0 is still flagged "loaded" from the
  // PREVIOUS form, so readiness would be reported for images that have not
  // decoded yet. A signature rather than `pages` itself: the array can be a new
  // reference on every render, and a raw dependency would loop.
  // Flags are keyed by signature instead of cleared in an effect: an image can
  // fire onLoad before that effect runs, and the reset then wiped its flag for
  // good (it never fires again), leaving "Loading report..." stuck.
  const pagesSignature = pages.map(p => (p.base64 || '').length).join(',');
  const loadKey = (index: number) => `${pagesSignature}:${index}`;

  // The form sits below the inspection details (and the whole editor is
  // collapsed while the Comments tab is up), so the canvas measures and
  // decodes off-screen — reporting "ready" before it is ever on
  // screen and leaving the paint after the user clicks uncovered. Gate readiness
  // on the panel ACTUALLY being visible, so the parent's spinner stays up until
  // the form is genuinely painted in front of the inspector.
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const node = scrollRef.current;
    const IO = (window as any).IntersectionObserver;
    if (!node || !IO) {
      setVisible(true); // No observer support — fall back to "always visible".
      return undefined;
    }
    const observer = new IO(
      // Latched: once painted in front of the inspector it stays ready.
      // Scrolling back up to the details must not re-raise the spinner.
      (entries: any[]) => setVisible(prev => prev || entries.some(e => e.isIntersecting)),
      { threshold: 0.01 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const fitZoom = width > 0 ? width / BASE_W : 0;
  const zoom = fitZoom * zoomStep;
  const pageStride = BASE_H * zoom + PAGE_GAP;

  // "Ready" = on screen, measured, and the first page image decoded. The parent
  // keeps its spinner up until this fires, so it covers both the multi-MB
  // download and the decode/paint that follow.
  const canvasReady = visible && zoom > 0 && (pages.length === 0 || !!loadedPages[loadKey(0)]);
  React.useEffect(() => {
    onReadyChange(canvasReady);
  }, [canvasReady, onReadyChange]);

  const pageRefs = React.useRef<Array<HTMLDivElement | null>>([]);

  React.useImperativeHandle(handleRef, () => ({
    scrollToPage: (index: number) => {
      const parent = findScrollParent(scrollRef.current);
      const pageNode = pageRefs.current[index];
      if (!parent || !pageNode) return;
      const offset = pageNode.getBoundingClientRect().top - parent.getBoundingClientRect().top;
      parent.scrollTop += offset - PAGE_JUMP_OFFSET;
    },
  }));

  // Report which page is in view so the Page tabs can follow along. A probe a
  // third of the way down the viewport makes the tab flip when a page genuinely
  // takes over the screen, not the instant its top edge appears.
  const onVisiblePageRef = React.useRef(onVisiblePage);
  onVisiblePageRef.current = onVisiblePage;
  React.useEffect(() => {
    const parent = findScrollParent(scrollRef.current);
    if (!parent) return undefined;
    let frame: number | null = null;
    const onScroll = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        const probe = parent.getBoundingClientRect().top + parent.clientHeight * 0.35;
        let index = 0;
        pageRefs.current.forEach((node, i) => {
          if (node && node.getBoundingClientRect().top <= probe) index = i;
        });
        onVisiblePageRef.current(index);
      });
    };
    parent.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      parent.removeEventListener('scroll', onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  const commitValue = (pageKey: string, key: string, value: string) => {
    setData({
      ...data,
      [pageKey]: { ...data[pageKey], [key]: { ...data[pageKey][key], text: value } },
    });
  };

  const commitCell = (
    pageKey: string,
    key: string,
    rowIndex: number,
    colIndex: number,
    value: string,
  ) => {
    const field = data[pageKey][key];
    setData({
      ...data,
      [pageKey]: {
        ...data[pageKey],
        [key]: {
          ...field,
          matrix: field.matrix.map((row: any[], r: number) => {
            if (r !== rowIndex) return row;
            return row.map((cell: any, c: number) =>
              (c === colIndex ? { ...cell, text: value } : cell));
          }),
        },
      },
    });
  };

  // --- counter badge --------------------------------------------------------
  // Rendered OUTSIDE the scaled stage so it stays a readable, constant size no
  // matter how far the page is zoomed in.
  const renderBadge = () => {
    if (!activeKey) return null;
    const { page: pageKey, key } = splitRefKey(activeKey);
    const pageIndex = Number(pageKey);
    const pageData = (data && data[pageKey]) || {};
    const item = pageData[key];
    if (!item || item.isStatic) return null;

    const el = buildElements(pageData, formFontPt(data)).find(
      e => e.key === key && e.kind === 'field',
    );
    if (!el) return null;

    const fitWidths = fitWidthsByPage[pageKey] || {};
    const text = `${item.text ?? ''}`;
    const baseFontPx = ptToPx(el.fontPt);
    const boxPx = mmLen(fitWidths[key] || PAGE_W_MM - el.leftMm - FIELD_RIGHT_MARGIN_MM);
    const { shrunk } = fitFont(text, baseFontPx, boxPx);

    const hasMax = item.max !== undefined && item.max !== null && `${item.max}`.trim() !== '';
    // Average glyph width from the real text where possible, else a reference
    // character — the capacity number is a guide, the fits/shrinks state is
    // measured from the actual string and is always correct.
    const avgCharPx = text.length
      ? measureText(text, baseFontPx) / text.length
      : measureText('n', baseFontPx);
    const capacity = hasMax ? Number(item.max) : capacityChars(boxPx, avgCharPx);

    const at = mmToPx(el.leftMm, el.topMm);
    return (
      <Badge
        style={{
          left: at.x * zoom,
          top: pageIndex * pageStride + (at.y + baseFontPx * 0.35) * zoom + 6,
        }}
        $warn={!hasMax && shrunk}
      >
        <BadgeName>{fieldDisplayName(key, item)}</BadgeName>
        <span>
          {text.length}/{capacity}
        </span>
        {!hasMax && <BadgeState>{shrunk ? 'shrinks' : 'fits'}</BadgeState>}
      </Badge>
    );
  };

  const renderElement = (pageKey: string, el: BuilderElement) => {
    const pageData = (data && data[pageKey]) || {};
    const fitWidths = fitWidthsByPage[pageKey] || {};
    const at = mmToPx(el.leftMm, el.topMm);
    const item = pageData[el.key];
    const composite = refKey(pageKey, el.key);
    const id = el.itemIndex === undefined ? composite : `${composite}#${el.itemIndex}`;

    if (el.kind === 'signature') {
      return (
        <SignatureBox
          key={id}
          style={{
            left: at.x,
            top: at.y,
            width: mmLen(el.wMm || 40),
            height: mmLen(el.hMm || 10),
          }}
        >
          Signature
        </SignatureBox>
      );
    }

    if (el.kind === 'checkbox') {
      const size = mmLen(el.fontPt * PT_TO_MM);
      const checked = `${el.text}`.trim() === 'x';
      // The Results row follows Activity Status; it is shown, never clicked.
      const locked = isStatusDrivenResult(el.key, item);
      // Only ring the options amber while a REQUIRED group is wholly
      // unanswered — once something is ticked the rest are choices, not
      // omissions. A locked row being empty is the status's doing.
      const groupEmpty = !locked && !isFieldFilled(item);
      const required = isRequiredField(el.key, item);
      return (
        <React.Fragment key={id}>
          {/* One star per group, on its first box. */}
          {required && el.itemIndex === 0 && (
            <RequiredBadge
              style={{ left: at.x - BADGE_PX * 0.6, top: at.y - size - BADGE_PX * 0.6 }}
            />
          )}
          <Control
            ref={el.itemIndex === 0 ? (node => registerRef(composite, node)) : undefined}
            type="button"
            $checked={checked}
            $empty={groupEmpty}
            $locked={locked}
            $missing={groupEmpty && required}
            $resultMissing={!locked && isResultUnanswered(el.key, item, data)}
            disabled={locked}
            title={
            locked
              ? `${fieldDisplayName(el.key, item)} — set from Activity Status`
              : `${fieldTitle(el.key, item)} — ${el.label}`
          }
            style={{ left: at.x, top: at.y - size, width: size, height: size }}
            onFocus={() => setActiveKey(composite)}
            onKeyDown={navKeyDown(composite, false)}
            onClick={() => {
            if (locked) return;
            // Pass the new markup explicitly: a click both changes and commits
            // in one handler, so onBlur() with no argument would fall back to
            // the pre-toggle markup captured by this render.
            const next = toggleGroupItem(data, pageKey, el.key, el.itemIndex as number, !checked);
            setData(next);
            onBlur(next);
          }}
          >
            {checked && <Tick style={{ fontSize: size * 0.9 }}>✕</Tick>}
          </Control>
        </React.Fragment>
      );
    }

    if (el.kind === 'matrix') {
      const cells = (item && item.matrix) || [];
      const baseCellPx = ptToPx(el.fontPt);
      const widths = el.colWidthsMm || [];
      const firstCell = cells[0] && cells[0][0];
      const firstAt = firstCell ? mmToPx(Number(firstCell.left), Number(firstCell.top)) : null;
      const matrixBadge = isRequiredField(el.key, item) && firstAt ? (
        <RequiredBadge
          key={`${composite}#required`}
          style={{
            left: firstAt.x - BADGE_PX * 0.6,
            top: topForBaseline(firstAt.y, baseCellPx) - BADGE_PX * 0.6,
          }}
        />
      ) : null;
      const cellInputs = cells.map((row: any[], rowIndex: number) =>
        row.map((cell: any, colIndex: number) => {
          const cellAt = mmToPx(Number(cell.left), Number(cell.top));
          const cellKey = cell.name || `${cell.left}_${cell.top}`;
          // Bounded by ITS OWN column, not by one shared gap. Every cell used to
          // be the uniform gap wide, so on a form with uneven columns the value
          // ran straight over the printed rule into the next box.
          const boxMm = Math.max((widths[colIndex] ?? el.gapHMm ?? 8) - CELL_GUTTER_MM, 1);
          const boxPx = mmLen(boxMm);
          const cellText = `${cell.text ?? ''}`;
          // Same shrink-to-fit the plain fields use, so what is typed matches
          // what prints.
          const { fontPx: cellFontPx } = fitFont(cellText, baseCellPx, boxPx);
          return (
            <FieldInput
              key={`${composite}#${cellKey}`}
              aria-label={`${fieldDisplayName(el.key, item)} row ${rowIndex + 1} column ${colIndex + 1}`}
              value={cellText}
              style={{
                left: cellAt.x,
                top: topForBaseline(cellAt.y, baseCellPx),
                width: boxPx,
                height: baseCellPx * LINE_HEIGHT,
                fontSize: cellFontPx,
                lineHeight: `${baseCellPx * LINE_HEIGHT}px`,
              }}
              onFocus={() => setActiveKey(composite)}
              onChange={e => {
                // Shrinking alone bottoms out at MIN_FONT_PT and then spills
                // again, so cap the value at what the column can still hold.
                if (exceedsMaxLength(e.target.value, cellCharLimit(boxPx, item))) return;
                commitCell(pageKey, el.key, rowIndex, colIndex, e.target.value);
              }}
              onKeyDown={navKeyDown(composite, true)}
              onBlur={() => onBlur()}
            />
          );
        }));
      return [matrixBadge, ...cellInputs];
    }

    const text = `${(item && item.text) ?? ''}`;
    const baseFontPx = ptToPx(el.fontPt);
    const widthMm = fitWidths[el.key] || PAGE_W_MM - el.leftMm - FIELD_RIGHT_MARGIN_MM;
    const boxPx = mmLen(widthMm);
    const { fontPx } = fitFont(text, baseFontPx, boxPx);

    if (item && item.isStatic) {
      if (!text.trim()) return null;
      return (
        <StaticText
          key={id}
          style={{ left: at.x, top: at.y - fontPx * BASELINE_RATIO, fontSize: fontPx }}
        >
          {text}
        </StaticText>
      );
    }

    // A multiline field wraps in the PDF, so it wraps here too — same box width,
    // same line count, so the preview keeps matching the output.
    if (item && item.multiline) {
      const maxLines = Math.max(1, Number(item.maxLines) || DEFAULT_MAX_LINES);
      const lineHeightPx = baseFontPx * PDF_LINE_HEIGHT;
      const required = isRequiredField(el.key, item);
      const fieldTop = topForBaseline(at.y, baseFontPx);
      return (
        <React.Fragment key={id}>
          {required && (
            <RequiredBadge style={{ left: at.x - BADGE_PX * 0.6, top: fieldTop - BADGE_PX * 0.6 }} />
          )}
          <FieldArea
            ref={node => registerRef(composite, node)}
            title={fieldTitle(el.key, item)}
            aria-label={fieldDisplayName(el.key, item)}
            aria-required={required}
            $empty={!text.trim()}
            $missing={required && !text.trim()}
            value={text}
            rows={maxLines}
            style={{
            left: at.x,
            top: fieldTop,
            width: boxPx,
            height: lineHeightPx * maxLines,
            fontSize: baseFontPx,
            lineHeight: `${lineHeightPx}px`,
          }}
            onFocus={() => setActiveKey(composite)}
            onKeyDown={navKeyDown(composite, false)}
            onChange={e => {
            if (exceedsMaxLength(e.target.value, item && item.max)) return;
            commitValue(pageKey, el.key, e.target.value);
          }}
            onBlur={() => onBlur()}
          />
        </React.Fragment>
      );
    }

    const required = isRequiredField(el.key, item);
    const fieldTop = topForBaseline(at.y, baseFontPx);
    return (
      <React.Fragment key={id}>
        {required && (
          <RequiredBadge style={{ left: at.x - BADGE_PX * 0.6, top: fieldTop - BADGE_PX * 0.6 }} />
        )}
        <FieldInput
          ref={node => registerRef(composite, node)}
          title={fieldTitle(el.key, item)}
          aria-label={fieldDisplayName(el.key, item)}
          aria-required={required}
          $empty={!text.trim()}
          $missing={required && !text.trim()}
          value={text}
          style={{
          left: at.x,
          top: fieldTop,
          width: boxPx,
          height: baseFontPx * LINE_HEIGHT,
          fontSize: fontPx,
          lineHeight: `${baseFontPx * LINE_HEIGHT}px`,
        }}
          onFocus={() => setActiveKey(composite)}
          onKeyDown={navKeyDown(composite, true)}
          onChange={e => {
          if (exceedsMaxLength(e.target.value, item && item.max)) return;
          commitValue(pageKey, el.key, e.target.value);
        }}
          onBlur={() => onBlur()}
        />
      </React.Fragment>
    );
  };

  return (
    <Scroll ref={scrollRef}>
      <Host ref={hostRef}>
        {pages.map((pageImage, index) => {
          const pageKey = `${index}`;
          const pageData = (data && data[pageKey]) || {};
          const loaded = !!loadedPages[loadKey(index)];
          // A form can hold more scanned pages than pages the Super Admin has
          // placed fields on, so this page renders as a bare scan. Say so:
          // silence here is indistinguishable from "the form is broken", and
          // has been reported as "this page does not save".
          const hasFields = buildElements(pageData, formFontPt(data)).length > 0;
          return (
            <PageBlock
              key={pageKey}
              ref={node => {
                pageRefs.current[index] = node;
              }}
              style={{ height: zoom ? BASE_H * zoom : undefined }}
            >
              <PageLabel>Page {index + 1}</PageLabel>
              <Stage style={{ width: BASE_W, height: BASE_H, transform: `scale(${zoom})` }}>
                <PageImage
                  src={`data:image/png;base64,${pageImage.base64}`}
                  alt={`Form page ${index + 1}`}
                  draggable={false}
                  onLoad={() => setLoadedPages(prev => ({ ...prev, [loadKey(index)]: true }))}
                  onError={() => setLoadedPages(prev => ({ ...prev, [loadKey(index)]: true }))}
                />
                {loaded &&
                  zoom > 0 &&
                  buildElements(pageData, formFontPt(data))
                    .map(el => renderElement(pageKey, el))}
              </Stage>
              {loaded && zoom > 0 && !hasFields && (
                <NoFieldsNote>No fields to fill on this page</NoFieldsNote>
              )}
              {!loaded && (
                <ImageLoading>
                  <Loader size={28} />
                </ImageLoading>
              )}
            </PageBlock>
          );
        })}

        {zoom > 0 && renderBadge()}
      </Host>
    </Scroll>
  );
});

export default FormFillCanvas;

const Scroll = styled.div`
  width: 100%;
  min-width: 0;
  /* Grows to the full height of its pages: the form sits below the inspection
     details on ONE page, and InspectionEditor's panel is the only vertical
     scrollbar. A bounded box here meant the panel scrolled a little, then
     handed over to this one, which feels broken. Horizontal scroll stays for
     zoom above 100%. */
  overflow-x: auto;
  overflow-y: hidden;
  min-height: 260px;
  background: var(--neutral-100);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 12px;
  box-sizing: border-box;
`;

const Host = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
`;

// One page's slot in the stack. Height is set inline from the zoom so the
// scroll distance per page is predictable — the badge is placed by that
// stride.
const PageBlock = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: ${PAGE_GAP}px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PageLabel = styled.div`
  position: absolute;
  top: 6px;
  right: 8px;
  z-index: 4;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  pointer-events: none;
`;

const Stage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
  background: var(--neutral-0);
  box-shadow: var(--shadow-xs);
`;

// Sits above the scan (Stage carries no z-index) but stays click-through so it
// can never get in the way of a field on a page that does have them.
const NoFieldsNote = styled.div`
  position: absolute;
  top: 6px;
  left: 8px;
  z-index: 4;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  pointer-events: none;
`;

const PageImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* object-fit must stay "fill" - overlayInfo stretches the page image to the
     full A4 page too, discarding its aspect ratio. Anything else would put the
     controls out of register with the generated PDF. */
  object-fit: fill;
  user-select: none;
  pointer-events: none;
`;

const ImageLoading = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--neutral-0);
`;

const StaticText = styled.span`
  position: absolute;
  font-family: ${PDF_FONT};
  line-height: 1;
  white-space: pre;
  color: #000;
  pointer-events: none;
`;

// Transparent so the printed form shows through, with a faint wash so a field is
// discoverable without hunting for it. Three looks: filled (blue), optional and
// blank (lighter blue), required and blank (amber outline + wash — what Create
// Report will ask for). Blanks used to wash red whether required or not, so a
// required field could not be told apart from an optional one.
//
// Colours are rgba rather than the solid --danger/--primary tokens on purpose:
// these sit on top of the scanned page and have to let it show through.
type FieldLook = { $empty?: boolean; $missing?: boolean };

// Amber = the theme's --warning-500 (#e0a012), as rgba so the scan shows through.
const AMBER = (alpha: number) => `rgba(224, 160, 18, ${alpha})`;
const BLUE = (alpha: number) => `rgba(11, 107, 155, ${alpha})`;

const fieldBackground = ({ $empty, $missing }: FieldLook) => {
  if ($missing) return AMBER(0.18);
  return $empty ? BLUE(0.04) : BLUE(0.08);
};

const fieldHoverBackground = ({ $empty, $missing }: FieldLook) => {
  if ($missing) return AMBER(0.28);
  return $empty ? BLUE(0.1) : BLUE(0.16);
};

const fieldShadow = ({ $empty, $missing }: FieldLook) => {
  if ($missing) return `0 0 0 2px ${AMBER(0.95)}`;
  return `inset 0 -1px 0 ${$empty ? BLUE(0.25) : BLUE(0.35)}`;
};

// The required marker, matching the builder's. Sits just off the field's
// top-left corner inside the scaled stage, so it moves and zooms with it.
const RequiredBadge = styled.span.attrs({ 'aria-hidden': true, children: '*' })`
  position: absolute;
  z-index: 3;
  width: ${BADGE_PX}px;
  height: ${BADGE_PX}px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--warning-500, #e0a012);
  color: #fff;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  pointer-events: none;
`;

const FieldInput = styled.input<FieldLook>`
  position: absolute;
  margin: 0;
  padding: 0 1px;
  border: none;
  border-radius: 1px;
  box-sizing: border-box;
  font-family: ${PDF_FONT};
  color: #000;
  background: ${fieldBackground};
  box-shadow: ${fieldShadow};
  transition: background-color 0.1s ease, box-shadow 0.1s ease;

  &:hover {
    background: ${fieldHoverBackground};
  }

  &:focus {
    outline: none;
    background: rgba(11, 107, 155, 0.14);
    box-shadow: 0 0 0 2px var(--primary-600);
  }
`;

// Same skin as FieldInput, but wrapping — mirrors a multiline field in the PDF.
const FieldArea = styled.textarea<FieldLook>`
  position: absolute;
  margin: 0;
  padding: 0 1px;
  border: none;
  border-radius: 1px;
  box-sizing: border-box;
  resize: none;
  overflow: hidden;
  font-family: ${PDF_FONT};
  color: #000;
  background: ${fieldBackground};
  box-shadow: ${fieldShadow};
  transition: background-color 0.1s ease, box-shadow 0.1s ease;

  &:hover {
    background: ${fieldHoverBackground};
  }

  &:focus {
    outline: none;
    background: rgba(11, 107, 155, 0.14);
    box-shadow: 0 0 0 2px var(--primary-600);
  }
`;

// Draws no box of its own — no square for a checkbox, no circle for a radio.
// The scanned form already prints its own boxes, so a control on top just
// doubled them up, and the stamped PDF (overlayInfo) shows a bare "x" too —
// for a radio as well as a checkbox. A ticked option is the ✕ alone;
// discoverability of an untouched option comes from the soft ring OUTSIDE it,
// which never prints.
const Control = styled.button<{
  $checked: boolean;
  $empty: boolean;
  $locked?: boolean;
  $missing?: boolean;
  $resultMissing?: boolean;
}>`
  position: absolute;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  cursor: ${({ $locked }) => ($locked ? 'default' : 'pointer')};
  box-shadow: ${({ $checked, $empty, $locked, $missing, $resultMissing }) => {
    if ($checked || $locked) return 'none';
    // Unanswered result group (city forms): red on every option until one is
    // ticked, and back to red if it is unticked.
    if ($resultMissing) return '0 0 0 2px rgba(212, 75, 63, 0.9)';
    if ($missing) return `0 0 0 2px ${AMBER(0.95)}`;
    return `0 0 0 2px ${BLUE($empty ? 0.25 : 0.3)}`;
  }};
  transition: box-shadow 0.1s ease;

  &:hover:not(:disabled) {
    box-shadow: ${({ $checked, $resultMissing }) =>
    ($resultMissing && !$checked ? '0 0 0 3px rgba(212, 75, 63, 1)' : '0 0 0 2px rgba(11, 107, 155, 0.6)')};
  }

  /* Focus ring for the KEYBOARD only. After a mouse click the box keeps focus,
     and a blue :focus ring covered the red of an option that was just
     unticked — it looked "not required" while its siblings were red. */
  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px var(--primary-600);
  }
`;

const Tick = styled.span`
  line-height: 1;
`;

const SignatureBox = styled.div`
  position: absolute;
  box-sizing: border-box;
  border: 1px dashed var(--primary-600);
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${PDF_FONT};
  font-size: 9px;
  font-style: italic;
  color: var(--primary-700);
  background: rgba(11, 107, 155, 0.06);
  pointer-events: none;
`;

const Badge = styled.div<{ $warn: boolean }>`
  position: absolute;
  z-index: 6;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--neutral-0);
  background: ${({ $warn }) => ($warn ? 'var(--danger-500)' : 'var(--neutral-800, #333)')};
  box-shadow: var(--shadow-xs);
`;

const BadgeName = styled.span`
  font-weight: 500;
  opacity: 0.75;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BadgeState = styled.span`
  font-weight: 500;
  opacity: 0.8;
`;
